import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { merge, Subject } from 'rxjs';
import { auditTime, filter, takeUntil } from 'rxjs/operators';
import { ITerminalStatus, TerminalStatusState } from '../models/production-plan.models';
import { ProductionPlanService } from '../production-plan.service';

@Component({
	selector: 'app-terminal-status-cell',
	templateUrl: './terminal-status-cell.component.html',
	styleUrls: ['./terminal-status-cell.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class TerminalStatusCellComponent implements OnInit, OnChanges, OnDestroy {
	@Input() orderNumber: number;
	@Input() position: number;
	@Input() terminalIds: string[] = [];

	terminalStatuses: ITerminalStatus[] = [];
	status: TerminalStatusState = TerminalStatusState.Irrelevant;
	private destroy$ = new Subject<void>();

	constructor(
		private service: ProductionPlanService,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		merge(this.service.terminalStatusChanged$.pipe(filter((status) => this.isEquals(status))), this.service.terminalStatusRowsLoaded$.pipe(filter((rowKeys) => rowKeys.has(this.rowKey))))
			.pipe(auditTime(100), takeUntil(this.destroy$))
			.subscribe(() => {
				this.updateStatus();
				this.cdr.markForCheck();
			});
	}

	private isEquals(status: ITerminalStatus): boolean {
		return status.orderNumber === this.orderNumber && status.position === this.position && this.terminalIds.includes(status.terminalId);
	}

	private get rowKey(): string {
		return this.service.getOrderPositionKey(this.orderNumber, this.position);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['orderNumber'] || changes['position'] || changes['terminalIds']) {
			this.refreshStatus();
		}
	}

	private refreshStatus(): void {
		if (this.orderNumber == null || this.position == null || this.terminalIds.length === 0) {
			this.terminalStatuses = [];
			this.status = TerminalStatusState.Irrelevant;
			return;
		}

		this.updateStatus();
	}

	private updateStatus(): void {
		this.terminalStatuses = this.service.getTerminalStatusesForCell(this.orderNumber, this.position, this.terminalIds);
		this.status = this.service.computeCellStatus(this.terminalStatuses);
	}

	get progressInfo(): { percent: number; text: string } {
		const items = this.terminalStatuses.filter((ts) => ts.status !== 'irrelevant').flatMap((ts) => ts.itemOperations.flatMap((op) => op.items));
		const total = items.length;
		const done = items.filter((i) => i.completed).length;
		const ready = items.filter((i) => i.ready).length;

		return {
			percent: this.getProgressPercent(this.status, total, done, ready),
			text:
				this.status === TerminalStatusState.InProgress // For now only show numbers when 'in progress'
					? this.getProgressText(this.status, total, done, ready)
					: '',
		};
	}

	private getProgressPercent(status: TerminalStatusState, total: number, done: number, ready: number): number {
		if (total === 0) return 0;
		const progress = this.getProgressValue(status, total, done, ready);
		return Math.round((progress / total) * 100);
	}

	private getProgressText(status: TerminalStatusState, total: number, done: number, ready: number): string {
		return `${this.getProgressValue(status, total, done, ready)}/${total}`;
	}

	private getProgressValue(status: TerminalStatusState, total: number, done: number, ready: number): number {
		if (status === TerminalStatusState.Ready) return ready;
		if (status === TerminalStatusState.Pending) return total;
		return done;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
